import {
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { isDefined } from '../utils/types.js';

export interface UseResizableDrawerProps {
  drawerPlacement: 'Left' | 'Right';
  localStorageKey: string;
  minimumDrawerWidthInPixels: number;
  maximumDrawerWidthInPixels?: number;
  containerElement?: HTMLElement | null;
  reservedWidthInPixels?: number;
}

/** Bounds persisted and interactive drawer widths to the available container. */
export function useResizableDrawer(props: UseResizableDrawerProps) {
  const {
    drawerPlacement,
    localStorageKey,
    minimumDrawerWidthInPixels,
    maximumDrawerWidthInPixels,
    containerElement,
    reservedWidthInPixels = 0,
  } = props;
  const [isResizingDrawer, setIsResizingDrawer] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(() => {
    const storedWidth = localStorage.getItem(localStorageKey);
    if (!isDefined(storedWidth)) {
      return minimumDrawerWidthInPixels;
    }
    const width = parseFloat(storedWidth);
    return Number.isFinite(width) ? width : minimumDrawerWidthInPixels;
  });
  const stopResizing = useRef<(() => void) | undefined>(undefined);
  const observedElement = containerElement ?? document.body;
  // A stable subscription keeps width renders from removing active drag listeners.
  const subscribeToContainerSize = useCallback(
    (onStoreChange: () => void) => {
      let previousContainerWidth = observedElement.clientWidth;
      const resizeObserver = new ResizeObserver(() => {
        if (observedElement.clientWidth !== previousContainerWidth) {
          previousContainerWidth = observedElement.clientWidth;
          stopResizing.current?.();
        }
        onStoreChange();
      });
      resizeObserver.observe(observedElement);
      return () => {
        resizeObserver.disconnect();
        stopResizing.current?.();
      };
    },
    [observedElement]
  );
  const containerWidthInPixels = useSyncExternalStore(
    subscribeToContainerSize,
    () => observedElement.clientWidth,
    () => observedElement.clientWidth
  );
  const maximumAllowedWidth = Math.max(
    0,
    Math.min(
      maximumDrawerWidthInPixels ?? containerWidthInPixels * 0.75,
      containerWidthInPixels - reservedWidthInPixels
    )
  );
  const minimumAllowedWidth = Math.min(minimumDrawerWidthInPixels, maximumAllowedWidth);

  // Active drag handlers close over the bounds they started with.
  useEffect(() => {
    stopResizing.current?.();
  }, [minimumAllowedWidth, maximumAllowedWidth]);

  function getAllowedWidth(width: number) {
    return Math.max(Math.min(width, maximumAllowedWidth), minimumAllowedWidth);
  }

  function onStartResizingDrawer(event: ReactMouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    stopResizing.current?.();
    setIsResizingDrawer(true);
    let previousPositionX = event.clientX;
    let resizedWidth = getAllowedWidth(drawerWidth);

    function onMouseMove(event: MouseEvent) {
      const deltaX =
        drawerPlacement === 'Left'
          ? event.clientX - previousPositionX
          : previousPositionX - event.clientX;
      previousPositionX = event.clientX;
      resizedWidth = getAllowedWidth(resizedWidth + deltaX);
      setDrawerWidth(resizedWidth);
    }
    function onMouseUp() {
      localStorage.setItem(localStorageKey, resizedWidth.toString());
      stopResizing.current?.();
    }
    stopResizing.current = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('blur', onMouseUp);
      stopResizing.current = undefined;
      setIsResizingDrawer(false);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    window.addEventListener('blur', onMouseUp);
  }

  return {
    isResizingDrawer,
    defaultWidth: minimumAllowedWidth,
    maximumDrawerWidth: maximumAllowedWidth,
    drawerWidth: getAllowedWidth(drawerWidth),
    setDrawerWidth,
    setDrawerWidthAndPersist: (width: number) => {
      const allowedWidth = getAllowedWidth(width);
      localStorage.setItem(localStorageKey, allowedWidth.toString());
      setDrawerWidth(allowedWidth);
    },
    onStartResizingDrawer,
    onResetDrawerWidth: () => {
      stopResizing.current?.();
      localStorage.removeItem(localStorageKey);
      setDrawerWidth(minimumDrawerWidthInPixels);
    },
  };
}
